
import React, { useEffect } from 'react';
import { Input } from '../../ui/input';
import { Search, User, MapPin } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { useDebounce } from '../../../hooks/useDebounce';
import { KYPPageState, KYPPageActions, Player } from '../../../lib/kyp-search/hooks/useKYPPageState';

interface PlayerSearchPhaseProps {
  state: KYPPageState;
  actions: KYPPageActions;
}

export const PlayerSearchPhase: React.FC<PlayerSearchPhaseProps> = ({ state, actions }) => {
  const { 
    searchQuery, 
    playerMatches, 
    isSearching 
  } = state;
  
  const { 
    setSearchQuery, 
    searchPlayers, 
    selectPlayer 
  } = actions;
  
  // Use debounce to delay search
  const debouncedQuery = useDebounce(searchQuery, 300);
  
  // Effect to trigger search when debounced query changes
  useEffect(() => {
    if (debouncedQuery) {
      searchPlayers(debouncedQuery);
    } else {
      // Clear results if query is empty
      searchPlayers('');
    }
  }, [debouncedQuery, searchPlayers]);
  
  // Handle player selection
  const handlePlayerClick = (player: Player) => {
    selectPlayer(player);
  };
  
  // Show results only when we have matches and no player is selected
  const showResults = playerMatches.length > 0 && !isSearching;
  
  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-[#CECFD2]">Player Search</h2>
      </div>
      
      <div className="mb-6 bg-[#121212] rounded-lg shadow-lg border border-gray-500 p-6">
      <div className="mb-4">
        <label htmlFor="playerName" className="block text-sm font-semibold mb-2 text-[#CECFD2]">
          Player Name:
        </label>
        <div className="relative w-full">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            id="playerName"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#1A1A1A] border-gray-500 placeholder-gray-400 text-[#CECFD2] pl-10"
            placeholder="Search for a player..."
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            aria-autocomplete="none"
            // This is a trick to completely disable browser's autocomplete
            name={`player-name-${Math.random().toString(36).substring(2, 9)}`}
          />
        </div>
      </div>
      
      {/* Search results dropdown */}
      <div className="relative">
        {/* Searching indicator */}
        {isSearching && (
          <div className="absolute top-0 left-0 right-0 z-10 mt-1 bg-[#1A1A1A] border border-gray-500 rounded-md shadow-lg p-3">
            <div className="text-sm text-[#CECFD2]">Searching...</div>
          </div>
        )}
        
        {/* Search results */}
        {showResults && (
          <div className="absolute top-0 left-0 right-0 z-10 mt-1 bg-[#1A1A1A] border border-gray-500 rounded-md shadow-lg max-h-96 overflow-y-auto">
            {playerMatches.map((player) => (
              <div
                key={player.id}
                className="p-3 hover:bg-[#191919] cursor-pointer border-b border-gray-500 last:border-b-0"
                onClick={() => handlePlayerClick(player)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="font-medium text-[#CECFD2]">{player.name}</div>
                      <div className="text-sm text-gray-400 flex items-center gap-2">
                        {player.club && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {player.club}
                          </span>
                        )}
                        {player.position && (
                          <Badge variant="outline" className="text-xs bg-transparent border-gray-500 text-gray-300">
                            {player.position}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {player.age && (
                    <Badge variant="secondary" className="text-xs bg-[#3d37cc] text-white">
                      {player.age}y
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* No results message */}
        {!isSearching && searchQuery.trim() !== '' && playerMatches.length === 0 && (
          <div className="absolute top-0 left-0 right-0 z-10 mt-1 bg-[#1A1A1A] border border-gray-500 rounded-md shadow-lg p-3">
            <div className="text-sm text-[#CECFD2]">No players found</div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};
